import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { CartPromos } from '#src/components/cart/CartPromos/index.tsx';
import { filterLivePromos } from '#src/lib/promotionalEvent.ts';

import type {
  LazyCartPromosQuery,
  LazyCartPromosQueryVariables,
  LazyCartPromos_cart,
} from './__generated__/index.graphql.ts';

type Props = {
  cart: LazyCartPromos_cart;
};

const LAZY_CART_PROMOS_QUERY = gql`
  query LazyCartPromosQuery($cartId: ID!, $sport: Sport!) {
    currentUser {
      slug
      myCart(sport: $sport) {
        id
        ...CartPromos_cart
      }
    }
  }
  ${CartPromos.fragments.cart}
` as TypedDocumentNode<LazyCartPromosQuery, LazyCartPromosQueryVariables>;

export const LazyCartPromos = ({ cart }: Props) => {
  const { activePromotionalEvents, sport } = cart;

  const livePromos = filterLivePromos(activePromotionalEvents, sport);

  const skip = !livePromos.length || !cart.id;
  const { data, loading } = useQuery(LAZY_CART_PROMOS_QUERY, {
    variables: {
      cartId: cart.id,
      sport: cart.sport,
    },
    skip,
  });

  if (skip || loading) return null;

  if (!data?.currentUser?.myCart) return null;

  return <CartPromos cart={data.currentUser.myCart} />;
};

LazyCartPromos.fragments = {
  cart: gql`
    fragment LazyCartPromos_cart on Cart {
      id
      sport
      activePromotionalEvents {
        slug
        endDate
        startDate
        sport
      }
    }
  ` as TypedDocumentNode<LazyCartPromos_cart>,
};
